/**
 * Surface content — 2.9 Blueprint configuration (`/library/blueprints`).
 *
 * ZERO literals in the component: every figure, name, id, route and sentence
 * the surface renders resolves through this module, `@/demo/data`, or the two
 * content modules this one reads.
 *
 * WHAT THIS SURFACE IS. A blueprint is not a document, it is a STRUCTURE the
 * group agrees on: fixed sections, required decisions, a data model, and rules
 * that decide standard from custom. It is configured per vertical and versioned
 * centrally, so nine retail projects produce one improving structure rather
 * than nine documents. Retail is the vertical authored here.
 *
 * READ, NEVER RE-STATED (the whole point of a configured blueprint is that the
 * project and the pack cannot disagree with it):
 *  - The pack identity, its assets and the improvements that arrived in v4 come
 *    from `content/templateDetail` (`PACK`, `CONTENTS`, `IMPROVEMENTS`) — the
 *    22 regression cases are that module's sentence, rendered here unchanged.
 *  - D-04 and D-05 come from `content/blueprint` (`OPEN_DECISIONS`): the same
 *    two questions this configuration REQUIRES are the two currently live on
 *    the hero project, with their real ageing clock. Their cost range is
 *    DERIVED from the options' effort labels, never typed a second time.
 *  - The 61% pre-fill is the portfolio's measured reuse rate (`PORTFOLIO`).
 *
 * AUTHORED ONCE, HERE (the deck names the field, not the value): the eight
 * sections and who fills each, the four standard-vs-custom rules, D-01 to D-03,
 * the chase/escalation thresholds, and the group base version this vertical
 * inherits from.
 *
 * WITHHELD, AND SAID SO ON SCREEN: the other verticals' blueprints (counted
 * from the portfolio, not opened) and the section bodies themselves, which are
 * filled per project.
 */

import { CONTENTS, IMPROVEMENTS, PACK } from '@/demo/content/templateDetail'
import { OPEN_DECISIONS } from '@/demo/content/blueprint'
import type { OpenDecision } from '@/demo/content/blueprint'
import { OFFICES, PORTFOLIO, PROJECTS, ROLES, agentById, heroProject, personById, thinSpace } from '@/demo/data'
import type { RoleKey } from '@/demo/data'

/* ── A sentence in which some words are real destinations ───────────────── */

export interface Fragment {
  text: string
  /** Present when this fragment is a link to a real route. */
  to?: string
}

const t = (text: string): Fragment => ({ text })
const a = (text: string, to: string): Fragment => ({ text, to })

/* ── People and agents this configuration names ─────────────────────────── */

const HERO = heroProject()
const PROCESS_MAPPER = agentById('process-mapper')
const DRAFTER = agentById('blueprint-drafter')
const ARCHITECT = personById('marc-dubois')

/** The vertical, lower-cased, for sentences that read it mid-line. */
const VERTICAL = PACK.industry.toLowerCase()

/* ── Routes (no href is built by hand in the component) ─────────────────── */

const agentHref = (agentId: string): string => `/library/agents/${agentId}`
const personHref = (personId: string): string => `/resources/people/${personId}`

export const HREFS = {
  projects: '/projects',
  pack: `/library/templates/${PACK.id}`,
  agents: '/library/agents',
  processMapper: agentHref(PROCESS_MAPPER.id),
  drafter: agentHref(DRAFTER.id),
  architect: personHref(ARCHITECT.id),
  heroBlueprint: `/project/${HERO.id}/blueprint`,
} as const

export const ANCHORS = {
  sections: 'sections',
  rules: 'standard-vs-custom',
  decisions: 'required-decisions',
} as const

/* ── Chrome + header ────────────────────────────────────────────────────── */

/** The layer this vertical inherits from — authored once, read everywhere. */
const GROUP_BASE_VERSION = 'v11'
const GROUP_BASE_LABEL = `group base ${GROUP_BASE_VERSION}`

export const PAGE = {
  moduleLabel: 'PX · Configuration',
  breadcrumb: `Configuration › Blueprints › ${PACK.industry} rollout`,
  contextPill: `${PACK.industry} · ${PACK.version}`.toUpperCase(),
  title: `${PACK.industry} rollout blueprint`,
  /** v4 · Odoo 19 · used on 9 projects · inherits group base v11 */
  meta: [
    t(`${PACK.version} · Odoo ${PACK.odooVersion} · `),
    a(`used on ${PACK.reuseCount} projects`, HREFS.projects),
    t(` · inherits ${GROUP_BASE_LABEL}`),
  ] as readonly Fragment[],
  standfirst:
    `A blueprint is a structure the group agrees on, not a document a consultant writes: fixed sections, ` +
    `required decisions, and rules that decide standard from custom. It is configured per vertical and ` +
    `versioned centrally, so ${PACK.reuseCount} ${VERTICAL} projects produce one improving structure rather ` +
    `than ${PACK.reuseCount} documents.`,
} as const

/* ── 1 · Sections ───────────────────────────────────────────────────────── */

export interface BlueprintSection {
  index: number
  name: string
  /** One line on what the section holds — never a second paragraph. */
  note: string
  /** Who fills it; agent names are links to the agent's configuration. */
  filledBy: readonly Fragment[]
  required: boolean
}

const CONSULTANT = 'Consultant'
const LOCAL_CONSULTANT = 'Local consultant'

export const SECTIONS: readonly BlueprintSection[] = [
  {
    index: 1,
    name: 'Scope & legal entities',
    note: 'What the group is buying, and which entities and stores it runs on.',
    filledBy: [t(CONSULTANT)],
    required: true,
  },
  {
    index: 2,
    name: 'Process maps',
    note: 'Order-to-cash and purchase-to-pay as they run today, before any design.',
    filledBy: [a(PROCESS_MAPPER.name, HREFS.processMapper), t(' agent')],
    required: true,
  },
  {
    index: 3,
    name: 'Solution design per epic',
    note: 'One design per epic, with the approach it takes and what that costs in code.',
    filledBy: [a(DRAFTER.name, HREFS.drafter)],
    required: true,
  },
  {
    index: 4,
    name: 'Standard vs custom',
    note: 'Every gap named, and the rule below applied to it in writing.',
    filledBy: [a(DRAFTER.name, HREFS.drafter), t(' + consultant')],
    required: true,
  },
  {
    index: 5,
    name: 'Data model & migration objects',
    note: 'The entity spine, and the object each one becomes at migration.',
    filledBy: [t('Migration lead')],
    required: true,
  },
  {
    index: 6,
    name: 'Integrations & interfaces',
    note: 'What talks to Odoo, in which direction, and who owns the failure.',
    filledBy: [t('Solution architect')],
    required: true,
  },
  {
    index: 7,
    name: 'Country specifics',
    note: 'Tax, fiscal and payroll rules the country layer adds on top.',
    filledBy: [t(LOCAL_CONSULTANT)],
    required: false,
  },
  {
    index: 8,
    name: 'Training & adoption',
    note: 'Who is trained, in which language, and on what the pack already ships.',
    filledBy: [t(LOCAL_CONSULTANT)],
    required: false,
  },
]

export const REQUIRED_SECTIONS = SECTIONS.filter((section) => section.required).length
export const CONDITIONAL_SECTIONS = SECTIONS.length - REQUIRED_SECTIONS

export const SECTIONS_TABLE = {
  label: `Sections every ${VERTICAL} blueprint contains`,
  columns: {
    index: '#',
    section: 'Section',
    filledBy: 'Filled by',
    required: 'Required',
  },
  requiredLabel: 'Required',
  conditionalLabel: 'Where the country layer applies',
  gateNote: [
    t('A section left empty blocks '),
    a('the Design gate', HREFS.heroBlueprint),
    t(
      ': the structure is the checklist, so an unfinished blueprint cannot be signed — and the client sees which ' +
        'section is missing, not a refusal.'
    ),
  ] as readonly Fragment[],
} as const

/* ── 2 · Standard vs custom ─────────────────────────────────────────────── */

export interface StandardCustomRule {
  key: string
  rule: readonly Fragment[]
  effect: readonly Fragment[]
}

/** The threshold above which a custom module stops being a consultant's call. */
const ESCALATION_HOURS = 20

export const RULES: readonly StandardCustomRule[] = [
  {
    key: 'escalate',
    rule: [t(`Custom module estimated over ${ESCALATION_HOURS} h`)],
    effect: [
      t('Escalates to the solution architect — '),
      a(ARCHITECT.name, HREFS.architect),
      t(' — before it can enter the build board.'),
    ],
  },
  {
    key: 'regressions',
    rule: [t('Any custom module')],
    effect: [
      t('Must ship with regression cases attached to the pack, so '),
      a(`the ${PACK.version} suite grows with every project`, HREFS.pack),
      t(' instead of with every release.'),
    ],
  },
  {
    key: 'no-core-override',
    rule: [t('A design that overrides a core Odoo model')],
    effect: [
      t('Refused as written: it is rewritten as an upgrade-safe extension, or it becomes a required decision.'),
    ],
  },
  {
    key: 'library-first',
    rule: [t('A requirement the library already solves')],
    effect: [
      t("The pack's asset is proposed before any new build is estimated — that is where the group's "),
      a(`${PORTFOLIO.reuseRatePct}% reuse rate`, HREFS.pack),
      t(' comes from.'),
    ],
  },
]

export const RULES_TABLE = {
  label: 'Rules · standard vs custom',
  columns: { rule: 'Rule', effect: 'Effect' },
  caption:
    `${RULES.length} rules, applied by the blueprint agent while it drafts and re-checked by the consultant who ` +
    'reviews. A rule is configuration, not advice: the section cannot be signed while it is broken.',
} as const

/* ── 3 · Required decisions ─────────────────────────────────────────────── */

export interface RequiredDecision {
  id: string
  title: string
  question: string
  /** Who decides — a client-side role, because the blueprint is per vertical. */
  owner: string
  /** Cheapest to dearest option, in build hours. */
  costRange: string
  /** When the clock starts, and what it triggers. */
  ageing: string
  /** Set when this decision is open on a real project right now. */
  live: { label: string; href: string } | null
}

const CHASE_DAYS = 5
const ESCALATE_DAYS = 10

const ageingLine = (start: string): string =>
  `${start} · chased at ${CHASE_DAYS} days · escalated at ${ESCALATE_DAYS}`

const NO_BUILD_HOURS = 0

/** Effort labels read `+21 h`; the range is derived, never typed twice. */
const hoursIn = (label: string | null): number => {
  if (label === null) return NO_BUILD_HOURS
  const match = /\d+/.exec(label)
  return match === null ? NO_BUILD_HOURS : Number(match[0])
}

const costRangeOf = (decision: OpenDecision): string => {
  const hours = decision.options.map((option) => hoursIn(option.effortLabel))
  const low = Math.min(...hours)
  const high = Math.max(...hours)
  return low === high ? `${high} h` : `${low}–${high} h`
}

const openDecision = (id: string): OpenDecision => {
  const found = OPEN_DECISIONS.find((decision) => decision.id === id)
  if (!found) throw new Error(`No open decision ${id} on the hero project`)
  return found
}

/** A decision this configuration requires, currently live on the hero project. */
const liveDecision = (id: string, owner: string, ageingStart: string): RequiredDecision => {
  const decision = openDecision(id)
  return {
    id: decision.id,
    title: decision.title,
    question: decision.question,
    owner,
    costRange: costRangeOf(decision),
    ageing: ageingLine(ageingStart),
    live: {
      label: `Open on ${HERO.client} · waiting ${decision.waitingLabel}`,
      href: HREFS.heroBlueprint,
    },
  }
}

export const REQUIRED_DECISIONS: readonly RequiredDecision[] = [
  {
    id: 'D-01',
    title: 'Entity structure',
    question: 'How many legal entities, and which stores sit under each?',
    owner: 'Client · finance lead',
    costRange: '0–40 h',
    ageing: ageingLine('Clock starts at the Discover gate'),
    live: null,
  },
  {
    id: 'D-02',
    title: 'Store hierarchy depth',
    question: 'Does assortment hang off regions only, or off store groups and stores as well?',
    owner: 'Client · retail operations',
    costRange: '0–16 h',
    ageing: ageingLine('Clock starts when the store list is loaded'),
    live: null,
  },
  {
    id: 'D-03',
    title: 'Price list scope',
    question: 'One price list per region, or one per region and sales channel?',
    owner: 'Client · commercial',
    costRange: '0–24 h',
    ageing: ageingLine('Clock starts at the first pricing workshop'),
    live: null,
  },
  liveDecision('D-04', 'Client · commercial', 'Clock starts when the promotions design is drafted'),
  liveDecision('D-05', 'Client · franchise board', 'Clock starts when the franchise stores are scoped'),
]

export const LIVE_DECISIONS = REQUIRED_DECISIONS.filter((decision) => decision.live !== null)

export const DECISIONS_TABLE = {
  label: 'Required decisions · raised to the client automatically',
  columns: {
    id: '#',
    decision: 'Decision',
    owner: 'Owner',
    cost: 'Cost range',
    ageing: 'Ageing clock',
    state: 'Live now',
  },
  costNote: 'cheapest option to dearest',
  notLiveLabel: '—',
  notLiveHint: 'Not open on a project right now',
  caption: [
    t(
      `A design question with no number is a conversation. Configured here, each of the ` +
        `${REQUIRED_DECISIONS.length} is raised the moment the section it belongs to is drafted, with an owner, ` +
        `a cost range and a clock that starts when it reaches the client — not when we wrote it. ` +
        `${LIVE_DECISIONS.length} of them are open right now on `
    ),
    a(HERO.client, HREFS.heroBlueprint),
    t('.'),
  ] as readonly Fragment[],
} as const

/* ── 4 · Panels ─────────────────────────────────────────────────────────── */

export interface PreLoadedAsset {
  key: string
  name: string
  phase: string
}

export const PRE_LOADED = {
  label: 'Pre-loaded assets',
  figureLabel: 'Pre-filled at kick-off',
  figure: `${PORTFOLIO.reuseRatePct}%`,
  qualifier: `${CONTENTS.length} assets · installed on ${PACK.reuseCount} projects`,
  lead: [
    t(`A new ${VERTICAL} project starts ${PORTFOLIO.reuseRatePct}% filled: installing `),
    a(`${PACK.name} ${PACK.version}`, HREFS.pack),
    t(' puts these into the blueprint before the first workshop.'),
  ] as readonly Fragment[],
  assets: CONTENTS.map(
    (asset): PreLoadedAsset => ({ key: asset.key, name: asset.asset, phase: asset.phaseLabel })
  ),
} as const

export interface InheritanceLayer {
  key: string
  label: string
  detail: string
  href?: string
}

export const INHERITANCE = {
  label: 'Inheritance',
  lead: 'Four layers, in order. Each one may add to the layer above it.',
  layers: [
    {
      key: 'group',
      label: `Group base ${GROUP_BASE_VERSION}`,
      detail: 'The sections every blueprint carries, whatever the vertical: scope, data model, integrations, gates.',
    },
    {
      key: 'vertical',
      label: `${PACK.industry} ${PACK.version}`,
      detail: 'The retail sections and the standard-vs-custom rules on this page.',
      href: HREFS.pack,
    },
    {
      key: 'country',
      label: 'Country layer',
      detail: `Tax, fiscal and payroll sections per office — ${OFFICES.map((office) => office.code).join(' · ')}.`,
    },
    {
      key: 'project',
      label: HERO.client,
      detail: 'Where the sections are filled, the rules are applied, and the decisions are answered.',
      href: HREFS.heroBlueprint,
    },
  ] as readonly InheritanceLayer[],
  rule: 'A layer can add a section. It can never remove a required one.',
} as const

export interface VersionChange {
  key: string
  change: string
  source: string
  href: string
  evidence: string
}

export const WHAT_CHANGED = {
  label: `What ${PACK.version} changed`,
  lead:
    `The vertical layer improves where a project sends something back. ${IMPROVEMENTS.length} changes came into ` +
    `${PACK.version}, each from a project that hit the problem first.`,
  changes: IMPROVEMENTS.map(
    (improvement): VersionChange => ({
      key: improvement.key,
      change: improvement.change,
      source: improvement.sourceClient,
      href: improvement.sourceHref,
      evidence: improvement.evidence,
    })
  ),
  fromLabel: 'from',
  closing: [
    t('A project proposes, the maintainer merges, and the '),
    a(`${PACK.version} the next rollout installs`, HREFS.pack),
    t(' is the merged one.'),
  ] as readonly Fragment[],
} as const

/* ── KPI row ────────────────────────────────────────────────────────────── */

export interface ConfigKpi {
  key: string
  label: string
  figure: string
  qualifier: string
}

export const kpis = (): readonly ConfigKpi[] => [
  {
    key: 'sections',
    label: 'Sections',
    figure: thinSpace(SECTIONS.length),
    qualifier: `${REQUIRED_SECTIONS} required · ${CONDITIONAL_SECTIONS} where the country layer applies`,
  },
  {
    key: 'decisions',
    label: 'Required decisions',
    figure: thinSpace(REQUIRED_DECISIONS.length),
    qualifier: `${LIVE_DECISIONS.map((decision) => decision.id).join(' · ')} open now on ${HERO.client}`,
  },
  {
    key: 'reuse',
    label: PRE_LOADED.figureLabel,
    figure: PRE_LOADED.figure,
    qualifier: PRE_LOADED.qualifier,
  },
]

/* ── Density ────────────────────────────────────────────────────────────── */

/** Verticals the portfolio runs that this page counts rather than opens. */
const OTHER_VERTICALS = [...new Set(PROJECTS.map((project) => project.vertical))].filter(
  (vertical) => vertical !== PACK.industry
)

// Two roles publish (deck §0.3). `super-admin`, added by TASK-9F6C8EBE, is a
// PLATFORM principal — it operates the tenancy, it does not publish firm IP —
// so this list is deliberately unchanged at two.
const PUBLISH_ROLE_KEYS: readonly RoleKey[] = ['expert', 'admin']

const PUBLISH_ROLE_LABELS = ROLES.filter((role) => PUBLISH_ROLE_KEYS.includes(role.key))
  .map((role) => role.label)
  .join(' and ')

export const densityNote = (): readonly Fragment[] => [
  t(
    `One vertical is shown in full: ${VERTICAL}. The other ${OTHER_VERTICALS.length} verticals in the portfolio ` +
      `(${OTHER_VERTICALS.join(', ')}) each carry their own blueprint and are counted here, not opened — and the ` +
      'section bodies are withheld everywhere, because a section is filled per project, not per vertical. ' +
      `Publishing a version is gated: ${PUBLISH_ROLE_LABELS} publish, everyone else proposes, which is the same ` +
      'governance pattern the '
  ),
  a('agent library', HREFS.agents),
  t(' uses for an agent version.'),
]
